import { Check, Copy, Mail, Phone } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { useMatterActionsOptional } from '@/components/contexts/MatterActionsContext';
import { formatDate } from '@/lib/utils';

type FieldType = 'email' | 'phone' | 'default';

interface InteractiveDisplayFieldProps {
  label: string;
  value: string | number | null | undefined;
  fieldType?: FieldType;
  multiline?: boolean;
}

// Helper to detect field type from label if not explicitly provided
const detectFieldType = (label: string): FieldType => {
  const lowerLabel = label.toLowerCase();
  if (lowerLabel.includes('email')) return 'email';
  if (lowerLabel.includes('phone')) return 'phone';
  return 'default';
};

// Helper to format phone number for display
const formatPhoneForDisplay = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');

  // Format as (XXX) XXX-XXXX for 10-digit US numbers
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  // Format as +X (XXX) XXX-XXXX for 11-digit numbers (with country code)
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }

  // Return original if doesn't match expected format
  return value;
};

export const InteractiveDisplayField: React.FC<
  InteractiveDisplayFieldProps
> = ({ label, value, fieldType: explicitFieldType, multiline = false }) => {
  const [copied, setCopied] = useState(false);
  const matterActions = useMatterActionsOptional();

  // Determine field type
  const fieldType = explicitFieldType || detectFieldType(label);

  // Handle empty values
  if (value === null || value === undefined || value === '') {
    return <span className="text-muted-foreground italic">Not provided</span>;
  }

  const stringValue = String(value);

  // Format value for display
  const displayValue = (() => {
    // Check if it's a date
    if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
      return formatDate(value);
    }

    // Format phone numbers
    if (fieldType === 'phone') {
      return formatPhoneForDisplay(stringValue);
    }

    return stringValue;
  })();

  // Handle click based on field type
  const handleClick = () => {
    switch (fieldType) {
      case 'email':
        if (matterActions) {
          matterActions.openEmailModal({ to: [stringValue] });
        } else {
          copyToClipboard();
        }
        break;
      case 'phone':
        if (matterActions) {
          matterActions.openCallModal({ phoneNumber: stringValue });
        } else {
          copyToClipboard();
        }
        break;
      default:
        copyToClipboard();
    }
  };

  // Copy to clipboard
  const copyToClipboard = () => {
    navigator.clipboard
      .writeText(stringValue)
      .then(() => {
        setCopied(true);
        toast.success('Copied to clipboard');
        setTimeout(() => setCopied(false), 2000);
      })
      .catch(() => {
        toast.error('Failed to copy');
      });
  };

  // Get the appropriate icon for the field type
  const getIcon = () => {
    if (copied) {
      return <Check className="h-2.5 w-2.5 text-green-600" />;
    }

    switch (fieldType) {
      case 'email':
        return matterActions ? (
          <Mail className="h-2.5 w-2.5" />
        ) : (
          <Copy className="h-2.5 w-2.5" />
        );
      case 'phone':
        return matterActions ? (
          <Phone className="h-2.5 w-2.5" />
        ) : (
          <Copy className="h-2.5 w-2.5" />
        );
      default:
        return <Copy className="h-2.5 w-2.5" />;
    }
  };

  // Get tooltip text
  const getTooltip = () => {
    switch (fieldType) {
      case 'email':
        return matterActions ? 'Click to send email' : 'Click to copy';
      case 'phone':
        return matterActions ? 'Click to call' : 'Click to copy';
      default:
        return 'Click to copy';
    }
  };

  // For multiline text, show with whitespace preserved
  if (multiline) {
    return (
      <button
        type="button"
        onClick={handleClick}
        className="group text-left whitespace-pre-wrap cursor-pointer"
        title={getTooltip()}
      >
        <span className="border-b border-dashed border-zinc-300 group-hover:border-zinc-500 group-hover:text-zinc-900 transition-colors">
          {displayValue}
        </span>
        <span className="ml-1 inline-flex opacity-0 group-hover:opacity-100 text-zinc-400 group-hover:text-zinc-600 transition-opacity">
          {getIcon()}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      className="group inline-flex items-center gap-1 text-left cursor-pointer"
      title={getTooltip()}
    >
      <span className="border-b border-dashed border-zinc-300 group-hover:border-zinc-500 group-hover:text-zinc-900 transition-colors">
        {displayValue}
      </span>
      <span className="opacity-0 group-hover:opacity-100 text-zinc-400 group-hover:text-zinc-600 transition-opacity">
        {getIcon()}
      </span>
    </button>
  );
};
